import React from 'react';
import styles from './ActionButtons.module.scss';
import Button from '@src/components/button/Button';
import Tooltip from '@src/components/tooltip/Tooltip';
import CopyToClipboard from 'react-copy-to-clipboard';
import { LordIconRow } from '@src/components/lord-icon';
import { IconCopy, IconEdit, IconDeleteUser } from '@src/constants';
/**
 * A reusable component for action buttons in admin tables
 * Includes edit, delete, copy, and view actions with consistent styling
 * 
 * Used in table columns like:
 * - NAME: Shows a copy icon to copy the account ID
 * - COUNTRY: Displays the country flag
 * - END DATE: Shows the account expiration date
 * - STATUS: Shows if the account is active or expired
 * - TRIAL: Shows if the account is a trial account
 * - ACTIONS: Shows edit, delete, copy, and view buttons
 */
export type ActionButtonsProps = {
  // Common props
  className?: string;
  tooltipPosition?: 'left' | 'right' | 'top' | 'bottom' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';
  
  // Edit action
  onEdit?: () => void;
  editTooltip?: string;
  showEdit?: boolean;
  
  // Delete action
  onDelete?: () => void;
  deleteTooltip?: string;
  showDelete?: boolean;
  
  // Copy action
  onCopy?: (text: string) => void;
  copyText?: string;
  copyTooltip?: string;
  showCopy?: boolean;
  
  // View action
  onView?: () => void;
  viewLabel?: string;
  showView?: boolean;
  
  // Custom actions
  customActions?: React.ReactNode;
  
  // Row hover effect props
  isRowHovered?: boolean;
  rowColor?: string;
  stroke?: number;
  rowStroke?: number;
};

/**
 * A reusable component for action buttons in admin tables
 * Includes edit, delete, copy, and view actions with consistent styling
 */
const ActionButtons: React.FC<ActionButtonsProps> = ({
  className,
  tooltipPosition = 'top',
  onEdit,
  editTooltip = 'Edit',
  showEdit = true,
  onDelete,
  deleteTooltip = 'Delete',
  showDelete = true,
  onCopy,
  copyText = '',
  copyTooltip = 'Copy',
  showCopy = true,
  onView,
  viewLabel = 'View',
  showView = true,
  customActions,
  isRowHovered = false,
  rowColor = '#FFB073',
  stroke,
  rowStroke
}) => {

  return (
    <div className={`${styles.actionsContainer} ${className || ''}`}>
      
      {/* NAME - Copy icon for copying account ID */}
      {showCopy && copyText && onCopy && (
        <Tooltip position={tooltipPosition} tooltip={copyTooltip}>
          <CopyToClipboard
            text={copyText}
            onCopy={() => onCopy(copyText)}>
            <div className={styles.iconWrapper}>
              <LordIconRow 
                src={IconCopy} 
                trigger="hover" 
                size={20}
                isRowHover={isRowHovered}
                rowColor={rowColor}
                {...(stroke !== undefined ? { stroke } : {})}
                {...(rowStroke !== undefined ? { rowStroke } : {})}
                colors={{primary: '#274649', secondary: '#FFB073'}}
              />
            </div>
          </CopyToClipboard>
        </Tooltip>
      )}
      
      {/* ACTIONS - Edit button */}
      {showEdit && onEdit && (
        <Tooltip position={tooltipPosition} tooltip={editTooltip}>
          <div className={styles.iconWrapper} onClick={onEdit}>
              <LordIconRow 
                src={IconEdit} 
                trigger="hover" 
                size={20}
                isRowHover={isRowHovered}
                rowColor={rowColor}
                {...(stroke !== undefined ? { stroke } : {})}
                {...(rowStroke !== undefined ? { rowStroke } : {})}
                colors={{primary: '#274649', secondary: '#FFB073'}}
              />
          </div>
        </Tooltip>
      )}
      
      {/* ACTIONS - Delete button */}
      {showDelete && onDelete && (
        <Tooltip position={tooltipPosition} tooltip={deleteTooltip}>
          <div className={styles.iconWrapper} onClick={onDelete}>
              <LordIconRow 
                src={IconDeleteUser} 
                trigger="hover" 
                size={20}
                isRowHover={isRowHovered}
                rowColor="#FF0000"
                {...(stroke !== undefined ? { stroke } : {})}
                {...(rowStroke !== undefined ? { rowStroke } : {})}
                colors={{primary: '#274649', secondary: '#FF0000'}}
              />
          </div>
        </Tooltip>
      )}
      
      {/* Custom actions (if provided) */}
      {customActions}
      
      {/* ACTIONS - View button (e.g., "View Schools") */}
      {showView && onView && (
        <Tooltip position={tooltipPosition} tooltip={viewLabel}>
          <Button 
            label={viewLabel} 
            className={`viewButton ${isRowHovered ? 'rowHovered' : ''}`} 
            onClick={onView}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default ActionButtons;
